<template>
  <main-template>
    <v-row class="mb-1">
      <v-col>
        <h1>
          <v-btn color="primary" x-small text @click="$router.go(-1)">
            <v-icon left>fa fa-angle-left</v-icon>
          </v-btn>
          Minhas Justificativas de Atraso
        </h1>
      </v-col>
    </v-row>

    <v-row class="mb-3">
      <v-col cols="auto">
        <v-btn color="primary" :to="{ name: 'colaboradores.atrasosJustificados.novo' }">
          <v-icon left>mdi-plus</v-icon>
          Justificar Atraso
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card outlined class="rounded-lg shadow-sm">
          <v-card-title
            class="py-3 px-4 bg-grey lighten-5 d-flex align-center justify-space-between"
          >
            <span class="subtitle-1 font-weight-bold grey--text text--darken-3">
              Histórico de Solicitações
            </span>
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Pesquisar motivo ou data..."
              single-line
              hide-details
              dense
              outlined
              style="max-width: 300px"
            ></v-text-field>
          </v-card-title>

          <v-divider></v-divider>

          <v-data-table
            :headers="headers"
            :items="justificativas"
            :search="search"
            :loading="loading"
            loading-text="Carregando justificativas..."
            no-data-text="Nenhuma justificativa de atraso registrada."
            class="elevation-0"
          >
            <template v-slot:item.data="{ item }">
              <span class="font-weight-medium">
                {{ formatarData(item.data) }}
              </span>
            </template>

            <template v-slot:item.hora_prevista="{ item }">
              <span>{{ item.hora_prevista || "-" }}</span>
            </template>

            <template v-slot:item.hora_registrada="{ item }">
              <span class="error--text font-weight-bold">{{ item.hora_registrada || "-" }}</span>
            </template>

            <template v-slot:item.tempo_atraso="{ item }">
              <v-chip x-small color="warning" class="font-weight-bold white--text">
                {{ item.tempo_atraso || formatarMinutos(item.minutos_atraso) }}
              </v-chip>
            </template>

            <template v-slot:item.motivo="{ item }">
              <span
                class="text-truncate d-inline-block"
                style="max-width: 250px"
                :title="item.motivo"
              >
                {{ item.motivo }}
              </span>
            </template>

            <template v-slot:item.anexo="{ item }">
              <v-btn
                v-if="item.anexo"
                color="info"
                text
                x-small
                class="font-weight-bold"
                @click="abrirAnexo(item.id)"
              >
                <v-icon left x-small>mdi-paperclip</v-icon>
                Ver Anexo
              </v-btn>
              <span v-else class="caption grey--text">Sem anexo</span>
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip
                small
                :color="getStatusColor(item.status)"
                class="font-weight-bold white--text text-capitalize"
              >
                {{ formatarStatus(item.status) }}
              </v-chip>
            </template>

            <template v-slot:item.avaliacao="{ item }">
              <div v-if="item.status !== 'pendente'">
                <div class="caption font-weight-bold grey--text text--darken-2">
                  {{ item.avaliador ? item.avaliador.name : "Gestor" }}
                </div>
                <div class="caption grey--text" v-if="item.observacao_avaliador">
                  "{{ item.observacao_avaliador }}"
                </div>
              </div>
              <span v-else class="caption grey--text">Aguardando análise</span>
            </template>

            <template v-slot:item.acoes="{ item }">
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn icon small color="primary" v-on="on" @click="visualizar(item)">
                    <v-icon small>mdi-eye</v-icon>
                  </v-btn>
                </template>
                <span>Visualizar</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn
                    icon
                    small
                    color="warning"
                    :disabled="item.status !== 'pendente'"
                    v-on="on"
                    @click="abrirEdicao(item)"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>Editar</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn
                    icon
                    small
                    color="error"
                    :disabled="item.status !== 'pendente'"
                    v-on="on"
                    @click="confirmarExclusao(item)"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>Excluir</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <atraso-justificado-detail-dialog v-model="dialogVisualizar" :atraso="itemVisualizado" />

    <v-dialog v-model="dialogEditar" max-width="600" persistent>
      <v-card v-if="itemEditado">
        <v-card-title>Editar Justificativa de Atraso</v-card-title>
        <v-card-subtitle class="pt-1">
          {{ formatarData(itemEditado.data) }} · Previsto {{ itemEditado.hora_prevista || "-" }} ·
          Registrado {{ itemEditado.hora_registrada || "-" }}
        </v-card-subtitle>
        <v-divider />
        <v-card-text class="pt-4">
          <v-form ref="formEditar" v-model="formEditarValido" lazy-validation>
            <v-textarea
              v-model="formEditar.motivo"
              label="Motivo da Justificativa *"
              outlined
              dense
              rows="3"
              counter="500"
              :rules="[
                (v) => !!v || 'O motivo é obrigatório',
                (v) => (v && v.trim().length >= 5) || 'O motivo deve conter no mínimo 5 caracteres',
              ]"
            ></v-textarea>
            <v-file-input
              v-model="formEditar.anexo"
              :label="itemEditado.anexo ? 'Substituir anexo (opcional)' : 'Anexo (opcional)'"
              outlined
              dense
              prepend-icon="mdi-paperclip"
              accept=".pdf,.png,.jpg,.jpeg"
              show-size
              truncate-length="25"
              hint="Formatos permitidos: PDF, Imagens (PNG/JPG). Tamanho máx: 10MB."
              persistent-hint
            ></v-file-input>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="salvando" @click="dialogEditar = false">Cancelar</v-btn>
          <v-btn
            color="primary"
            :loading="salvando"
            :disabled="!formEditarValido || salvando"
            @click="salvarEdicao"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogExcluir" max-width="420">
      <v-card>
        <v-card-title>Confirmar Exclusão</v-card-title>
        <v-card-text>
          Deseja excluir a justificativa de atraso de
          <strong>{{ formatarData(itemParaExcluir && itemParaExcluir.data) }}</strong
          >? Esta ação não pode ser desfeita.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="excluindo" @click="dialogExcluir = false">Cancelar</v-btn>
          <v-btn color="error" :loading="excluindo" @click="excluir">Excluir</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </main-template>
</template>

<script>
import AtrasoJustificadoDetailDialog from "@/components/Colaboradores/AtrasoJustificadoDetailDialog.vue";

export default {
  name: "AtrasosJustificadosPage",
  components: {
    AtrasoJustificadoDetailDialog,
  },
  data() {
    return {
      loading: false,
      dialogVisualizar: false,
      itemVisualizado: null,
      dialogEditar: false,
      itemEditado: null,
      formEditarValido: false,
      formEditar: { motivo: "", anexo: null },
      salvando: false,
      dialogExcluir: false,
      itemParaExcluir: null,
      excluindo: false,
      search: "",
      justificativas: [],
      headers: [
        { text: "Data", value: "data", sortable: true },
        { text: "Hora Prevista", value: "hora_prevista", sortable: false },
        { text: "Hora Registrada", value: "hora_registrada", sortable: false },
        { text: "Atraso", value: "tempo_atraso", sortable: true },
        { text: "Motivo", value: "motivo", sortable: false },
        { text: "Anexo", value: "anexo", sortable: false },
        { text: "Status", value: "status", sortable: true },
        { text: "Análise", value: "avaliacao", sortable: false },
        { text: "", value: "acoes", sortable: false, align: "center", width: "120" },
      ],
    };
  },
  mounted() {
    this.carregarJustificativas();
  },
  methods: {
    async carregarJustificativas() {
      try {
        this.loading = true;
        const res = await this.$services.colaboradorPontosService.listarMeusAtrasosJustificados();
        this.justificativas = Array.isArray(res) ? res : [];
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.loading = false;
      }
    },
    visualizar(item) {
      this.itemVisualizado = item;
      this.dialogVisualizar = true;
    },
    abrirEdicao(item) {
      this.itemEditado = item;
      this.formEditar = { motivo: item.motivo || "", anexo: null };
      this.dialogEditar = true;
    },
    async salvarEdicao() {
      if (!this.$refs.formEditar.validate()) return;

      try {
        this.salvando = true;
        const formData = new FormData();
        formData.append("motivo", this.formEditar.motivo);
        if (this.formEditar.anexo) {
          formData.append("anexo", this.formEditar.anexo);
        }

        await this.$services.colaboradorPontosService.atualizarMeuAtrasoJustificado(
          this.itemEditado.id,
          formData
        );
        this.$toast.success("Justificativa atualizada com sucesso!");
        this.dialogEditar = false;
        await this.carregarJustificativas();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.salvando = false;
      }
    },
    confirmarExclusao(item) {
      this.itemParaExcluir = item;
      this.dialogExcluir = true;
    },
    async excluir() {
      try {
        this.excluindo = true;
        await this.$services.colaboradorPontosService.excluirMeuAtrasoJustificado(
          this.itemParaExcluir.id
        );
        this.$toast.success("Justificativa excluída com sucesso!");
        this.dialogExcluir = false;
        await this.carregarJustificativas();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.excluindo = false;
      }
    },
    async abrirAnexo(id) {
      try {
        await this.$services.colaboradorPontosService.abrirMeuAnexoAtraso(id);
      } catch (error) {
        this.$handleError(error);
      }
    },
    formatarData(dataStr) {
      if (!dataStr) return "-";
      const partes = dataStr.split("-");
      if (partes.length === 3) {
        return `${partes[2]}/${partes[1]}/${partes[0]}`;
      }
      return dataStr;
    },
    formatarMinutos(minutos) {
      if (!minutos) return "00h00m";
      const h = Math.floor(minutos / 60);
      const m = minutos % 60;
      return `${String(h).padStart(2, "0")}h${String(m).padStart(2, "0")}m`;
    },
    formatarStatus(status) {
      const map = {
        pendente: "Pendente",
        aprovado: "Aprovada",
        rejeitado: "Rejeitada",
      };
      return map[status] || status;
    },
    getStatusColor(status) {
      const map = {
        pendente: "amber darken-2",
        aprovado: "success",
        rejeitado: "error",
      };
      return map[status] || "grey";
    },
  },
};
</script>
